import { useEffect, useRef, useState } from "react";
import { api } from "../../api";
import { ErrorNote, Loading } from "../../components/Feedback";
import { Badge, Btn, Field, G_DARK, Input, Textarea } from "../../components/Shared";
import { rememberFocus } from "../../focus";
import { AskDialog, SuccessNote } from "./parts";

// Management -> E-mail -> Templates: every e-mail unio sends on its own (the invitation, the
// password reset, the test message, one per notification) has a subject and a body here
// (GET/PATCH /api/admin/mail/templates, POST .../preview, POST .../{kind}/test).
// - {{placeholder}} is replaced by its value; an unknown one stays as written (the preview
//   names it). Required placeholders ({{link}} of the account mails) must be in the body.
// - Saving text equal to the built-in default stores nothing, so a later default reaches it;
//   "Reset to default" deletes the saved text.
// - The preview is the server's rendering with sample values, asked for PREVIEW_MS after the
//   last keystroke. "Send test" sends what is in the editor, saved or not, to the admin.

const URL = "/admin/mail/templates";
export const PREVIEW_MS = 400;

function groups(templates) {
  const out = [];
  for (const t of templates) {
    let g = out.find(x => x.name === t.group);
    if (!g) { g = { name: t.group, items: [] }; out.push(g); }
    g.items.push(t);
  }
  return out;
}

export default function MailTemplates({ configured }) {
  const [templates, setTemplates] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [kind, setKind] = useState("");
  const [draft, setDraft] = useState({ subject: "", body: "" });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(null);           // null | "save" | "reset" | "test"
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [result, setResult] = useState(null);
  const [pending, setPending] = useState(null);     // the kind picked while there are unsaved edits
  const buttonsRef = useRef(null);

  useEffect(() => {
    let live = true;
    api.get(URL)
      .then(r => {
        if (!live) return;
        setTemplates(r.templates);
        if (r.templates.length) {
          setKind(r.templates[0].kind);
          setDraft({ subject: r.templates[0].subject, body: r.templates[0].body });
        }
      })
      .catch(e => { if (live) setLoadError(e); });
    return () => { live = false; };
  }, []);

  // The debounced preview of what is in the editor.
  useEffect(() => {
    if (!kind) return undefined;
    let live = true;
    const timer = setTimeout(() => {
      api.post(`${URL}/preview`, { kind, subject: draft.subject, body: draft.body })
        .then(p => { if (live) setPreview(p); })
        .catch(e => { if (live) setPreview({ error: e.message }); });
    }, PREVIEW_MS);
    return () => { live = false; clearTimeout(timer); };
  }, [kind, draft.subject, draft.body]);

  if (loadError) return <ErrorNote error={loadError} />;
  if (!templates) return <Loading label="Loading the templates…" />;

  const current = templates.find(t => t.kind === kind);
  const dirty = !!current && (draft.subject !== current.subject || draft.body !== current.body);
  const atDefault = !!current && draft.subject === current.default_subject && draft.body === current.default_body;

  const pick = e => {
    if (e.target.value === kind) return;
    if (dirty) { setPending(e.target.value); return; }       // ask before discarding the edits
    open(e.target.value);
  };

  const open = next => {
    const t = templates.find(x => x.kind === next);
    setPending(null);
    setKind(t.kind);
    setDraft({ subject: t.subject, body: t.body });
    setPreview(null);
    setError(null);
    setNotice(null);
    setResult(null);
  };

  const replaceAll = list => {
    setTemplates(list);
    const t = list.find(x => x.kind === kind);
    if (t) setDraft({ subject: t.subject, body: t.body });
  };

  const run = async (what, fn) => {
    setError(null);
    setNotice(null);
    setResult(null);
    const restore = rememberFocus();
    setBusy(what);
    try {
      await fn();
    } catch (e) {
      if (what === "test") setResult({ ok: false, detail: e.message });
      else setError(e);
    } finally {
      setBusy(null);
      restore(() => buttonsRef.current && buttonsRef.current.querySelector("button"));
    }
  };

  const save = () => run("save", async () => {
    const r = await api.patch(URL, { templates: [{ kind, subject: draft.subject, body: draft.body }] });
    replaceAll(r.templates);
    const saved = r.templates.find(x => x.kind === kind);
    setNotice(saved && !saved.customized
      ? "Saved. This is the built-in text, so nothing is stored and later versions of it apply."
      : "Saved. The next e-mail of this kind uses it, also one that is already waiting to be sent.");
  });

  const reset = () => run("reset", async () => {
    const r = await api.patch(URL, { templates: [{ kind, reset: true }] });
    replaceAll(r.templates);
    setNotice("Back to the built-in text.");
  });

  const sendTest = () => run("test", async () => {
    const r = await api.post(`${URL}/${kind}/test`, { subject: draft.subject, body: draft.body });
    setResult({ ok: !!r?.ok, detail: r?.detail || "" });
  });

  return (
    <section aria-labelledby="mail-templates-title" style={{ marginTop: 28 }}>
      <h3 id="mail-templates-title" style={{ margin: "0 0 4px", fontSize: 15, fontWeight: 700 }}>Templates</h3>
      <p style={{ margin: "0 0 12px", fontSize: 13, color: "#4b5563" }}>
        Every e-mail the portal sends on its own, as plain text. Write it in any language;
        {" "}<code>{"{{placeholder}}"}</code> is replaced by its value. The notification in the
        portal's bell keeps its own short text. Keycloak's e-mails (verify e-mail, reset
        credentials) are edited in the Keycloak theme, not here.
      </p>

      <ErrorNote error={error} />
      <SuccessNote>{notice}</SuccessNote>

      <Field label="E-mail">
        <select value={kind} onChange={pick}
                style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14, fontFamily: "inherit", background: "#fff" }}>
          {groups(templates).map(g => (
            <optgroup key={g.name} label={g.name}>
              {g.items.map(t => (
                <option key={t.kind} value={t.kind}>{t.label}{t.customized ? " (edited)" : ""}</option>
              ))}
            </optgroup>
          ))}
        </select>
      </Field>

      {pending && (
        <AskDialog title="Discard your changes?" confirmLabel="Discard and switch"
                   onConfirm={() => open(pending)} onClose={() => setPending(null)}>
          <p style={{ margin: 0, fontSize: 14, color: "#374151" }}>
            “{current?.label}” has changes that are not saved. Switching to
            “{templates.find(t => t.kind === pending)?.label}” discards them.
          </p>
        </AskDialog>
      )}

      {current && (
        <div style={{ marginTop: 14 }}>
          <div style={{ marginBottom: 10 }}>
            <Badge color={current.customized ? "green" : "gray"}>
              {current.customized ? "Edited" : "Built-in text"}
            </Badge>
            {dirty && <span style={{ marginLeft: 8, fontSize: 12, color: "#92400e" }}>Unsaved changes</span>}
          </div>
          {/* min(300px, 100%): on a 320 px phone the content box is narrower than 300 px, and a
              300 px minimum made the page scroll sideways (a11y check of 2026-10-21). */}
          <div data-mail-editor style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(min(300px, 100%), 1fr))", alignItems: "start" }}>
            <div style={{ display: "grid", gap: 12 }}>
              <Field label="Subject">
                <Input value={draft.subject} spellCheck={false}
                       onChange={e => setDraft(d => ({ ...d, subject: e.target.value }))} />
              </Field>
              <Field label="Body">
                <Textarea value={draft.body} rows={14} spellCheck={false}
                          onChange={e => setDraft(d => ({ ...d, body: e.target.value }))} />
              </Field>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", letterSpacing: "0.05em", textTransform: "uppercase", marginBottom: 6 }}>
                  Placeholders
                </div>
                <ul aria-label="Placeholders" style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: "#374151" }}>
                  {current.placeholders.map(p => (
                    <li key={p.name} style={{ marginBottom: 2 }}>
                      <code>{`{{${p.name}}}`}</code>{p.required && <strong> (required)</strong>} — {p.description}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", letterSpacing: "0.05em", textTransform: "uppercase", marginBottom: 6 }}>
                Preview with sample values
              </div>
              <div aria-live="polite" data-testid="mail-preview"
                   style={{ border: "1px solid #e5e7eb", borderRadius: 8, background: "#f9fafb", padding: "10px 13px", fontSize: 13 }}>
                {!preview && <span style={{ color: "#6b7280" }}>…</span>}
                {preview?.error && <span style={{ color: "#b91c1c" }}>{preview.error}</span>}
                {preview && !preview.error && (
                  <>
                    <div style={{ fontWeight: 600, marginBottom: 8 }}>Subject: {preview.subject}</div>
                    <pre style={{ margin: 0, whiteSpace: "pre-wrap", fontFamily: "inherit" }}>{preview.body}</pre>
                  </>
                )}
              </div>
              {preview?.unknown?.length > 0 && (
                <p style={{ margin: "8px 0 0", fontSize: 12, color: "#92400e" }}>
                  Not a placeholder of this e-mail, sent as written: {preview.unknown.map(u => `{{${u}}}`).join(", ")}
                </p>
              )}
              {preview?.problems?.length > 0 && (
                <ul style={{ margin: "8px 0 0", paddingLeft: 18, fontSize: 12, color: "#b91c1c" }}>
                  {preview.problems.map(p => <li key={p}>{p}</li>)}
                </ul>
              )}
            </div>
          </div>

          <div ref={buttonsRef} style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <Btn onClick={save} disabled={!!busy || !dirty}>{busy === "save" ? "Saving…" : "Save template"}</Btn>
            <Btn variant="secondary" onClick={sendTest} disabled={!!busy || !configured}>
              {busy === "test" ? "Sending…" : "Send test"}
            </Btn>
            <Btn variant="secondary" onClick={() => setDraft({ subject: current.subject, body: current.body })}
                 disabled={!!busy || !dirty}>
              Discard changes
            </Btn>
            <Btn variant="secondary" onClick={reset} disabled={!!busy || (!current.customized && atDefault)}>
              {busy === "reset" ? "Resetting…" : "Reset to default"}
            </Btn>
          </div>
          <p style={{ margin: "8px 0 0", fontSize: 12, color: "#4b5563" }}>
            Send test mails what is in the editor, saved or not, with the sample values, to your own
            address{configured ? "" : " (connect an SMTP relay above first)"}.
          </p>
          {result && (
            <div role="status" style={{ marginTop: 10, fontSize: 13, color: result.ok ? G_DARK : "#b91c1c" }}>
              <span aria-hidden="true">{result.ok ? "✓ " : "✗ "}</span>
              {result.detail}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
